import { useCallback, useEffect, useMemo } from "react"
import { LeaderboardEntry } from "@mezo/api"
import { useStyletron } from "@mezo-org/mezo-clay"

import trophyBlue from "#/assets/trophy-blue.svg"
import trophyGold from "#/assets/trophy-gold.svg"
import trophyRed from "#/assets/trophy-red.svg"
import {
  fetchLeaderboard,
  selectFullLeaderboard,
  selectIsLeaderboardFullyFetched,
  selectLeaderboardRankedUsers,
  selectUserLeaderboardRank,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import { LEADERBOARD_FETCH_CHUNK } from "#/shared/constants"

import { LeaderboardProps, LeaderboardRank } from "../types"

import { useStatistics } from "./statistics"

export const useLeaderboard = (): LeaderboardProps<LeaderboardEntry> => {
  const leaderboard = useDappSelector(selectFullLeaderboard)
  const userRank = useDappSelector(selectUserLeaderboardRank)

  const isLeaderboardFullyFetched = useDappSelector(
    selectIsLeaderboardFullyFetched,
  )

  const dispatch = useDappDispatch()

  useEffect(() => {
    const loadLeaderboard = async () => {
      if (leaderboard.length) return

      await dispatch(
        fetchLeaderboard({ start: 0, end: LEADERBOARD_FETCH_CHUNK }),
      )
    }

    loadLeaderboard()
  }, [dispatch, leaderboard])

  const loadMoreHandler = useCallback(async () => {
    await dispatch(
      fetchLeaderboard({
        start: leaderboard.length,
        end: leaderboard.length + LEADERBOARD_FETCH_CHUNK,
      }),
    )
  }, [dispatch, leaderboard.length])

  return {
    leaderboard,
    isLeaderboardFullyFetched,
    loadMore: loadMoreHandler,
    currentUserRank: userRank.rank,
  }
}

export const useLeaderboardRankCalculations = (
  rank: number | null,
  numberOfRankedUsers: number,
): LeaderboardRank => {
  const [, theme] = useStyletron()

  const place = useMemo(() => {
    if (!rank) return null

    return rank / numberOfRankedUsers
  }, [numberOfRankedUsers, rank])

  const rankData = useMemo(() => {
    if (!place) return null

    if (place <= 0.1) {
      return {
        trophy: trophyRed,
        text: "Top 10%",
        color: theme.colors.red600,
      }
    }

    if (place <= 0.25) {
      return { trophy: trophyGold, text: "Top 25%", color: "#F1A91F" }
    }

    if (place <= 0.5) {
      return { trophy: trophyBlue, text: "Top 50%", color: "#72C0BD" }
    }

    return null
  }, [place, theme])

  return { place, rank, rankData }
}

export const useLeaderboardRank = (): LeaderboardRank => {
  const { rank } = useDappSelector(selectUserLeaderboardRank)
  const numberOfRankedUsers = useDappSelector(selectLeaderboardRankedUsers)

  useStatistics()

  const rankData = useLeaderboardRankCalculations(rank, numberOfRankedUsers)

  return rankData
}
